/**
 * ChartManager - Centralized Chart Loading and Management Service
 * Professional refactor to eliminate duplication between Header.tsx and MultipleChartsView.tsx
 * Architecture: v4.1.0 - Hash-based duplicate detection via metadata.contentHash
 */

import { devLog, devError } from '../utils/devLog';
import { ParsedChartData } from '../models/types';
import { ChartMetadata } from '../models/storage';
import { getStorageService } from './StorageService';
import { emitChartAccessed, emitChartSaved } from '../hooks/useRecentFiles';

export interface ChartLoadOptions {
  makeActive?: boolean;
  insertAtIndex?: number;
  skipDuplicateCheck?: boolean;
  showToast?: boolean;
}

export interface ChartCreateOptions {
  chartName?: string;
  makeActive?: boolean;
  insertAtIndex?: number;
  showToast?: boolean;
}

export interface LoadResult {
  success: boolean;
  chartId?: string;
  error?: string;
  isDuplicate?: boolean;
  message?: string;
}

export interface ToastConfig {
  title: string;
  description: string;
  status: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
}

/**
 * Centralized Chart Management Service
 * Handles all chart loading operations across the application
 */
export class ChartManager {
  private loadChartToWorkspace: (chartId: string, options?: any, preloadedData?: any) => Promise<void>;
  private loadedChartIds: string[];
  private workspace: { maxOpenCharts: number };
  private showToast: (config: ToastConfig) => void;
  private getLoadedContentHashes: () => Map<string, string>;

  constructor(
    loadChartToWorkspace: (chartId: string, options?: any, preloadedData?: any) => Promise<void>,
    loadedChartIds: string[],
    workspace: { maxOpenCharts: number },
    showToast: (config: ToastConfig) => void,
    getLoadedContentHashes: () => Map<string, string> = () => new Map()
  ) {
    this.loadChartToWorkspace = loadChartToWorkspace;
    this.loadedChartIds = loadedChartIds;
    this.workspace = workspace;
    this.showToast = showToast;
    this.getLoadedContentHashes = getLoadedContentHashes;
  }

  /**
   * Helper method to conditionally show toast notifications (DRY)
   */
  private notify(
    show: boolean,
    title: string,
    description: string,
    status: ToastConfig['status'],
    duration = 3000
  ): void {
    if (show) {
      this.showToast({ title, description, status, duration });
    }
  }

  /**
   * Universal chart loading from storage ID
   * Handles duplicates, workspace capacity, and error management
   */
  async loadChartFromStorage(
    chartId: string,
    metadata: ChartMetadata,
    options: ChartLoadOptions = {}
  ): Promise<LoadResult> {
    const {
      makeActive = true,
      insertAtIndex,
      skipDuplicateCheck = false,
      showToast = true
    } = options;

    try {
      // Check workspace capacity
      if (this.loadedChartIds.length >= this.workspace.maxOpenCharts) {
        const result = {
          success: false,
          error: `Maximum ${this.workspace.maxOpenCharts} charts allowed. Close a chart first.`
        };

        this.notify(showToast, 'Workspace Full', result.error, 'warning', 6000);
        return result;
      }

      // Check if already loaded (unless skipped)
      if (!skipDuplicateCheck && this.loadedChartIds.includes(chartId)) {
        const result = {
          success: false,
          isDuplicate: true,
          error: `Chart "${metadata.name}" is already open in workspace`
        };

        this.notify(showToast, 'Chart Already Loaded', result.error, 'info');
        return result;
      }

      // Load chart data from storage
      const storageService = getStorageService();
      const result = await storageService.loadChart(chartId);

      if (!result.success || !result.data) {
        throw new Error(result.error || 'Failed to load chart data');
      }

      // Check for contentHash (required since v4.1)
      const contentHash = result.data.metadata.contentHash;
      if (!contentHash) {
        const outdatedResult = {
          success: false,
          error: `Chart "${metadata.name}" is outdated and missing content hash. Please delete and recreate this chart.`
        };
        this.notify(showToast, 'Outdated Chart', outdatedResult.error, 'error', 8000);
        return outdatedResult;
      }

      // Check for content duplicate using hash (if not skipped)
      if (!skipDuplicateCheck) {
        const loadedHashes = this.getLoadedContentHashes();
        for (const [existingChartId, existingHash] of loadedHashes) {
          if (existingHash === contentHash && existingChartId !== chartId) {
            const duplicateResult = {
              success: false,
              isDuplicate: true,
              error: `This chart content is already open in workspace`
            };
            this.notify(showToast, 'Duplicate Chart', duplicateResult.error, 'info', 4000);
            return duplicateResult;
          }
        }
      }

      // Load chart into workspace with pre-loaded data (avoids redundant storage call)
      await this.loadChartToWorkspace(
        chartId,
        {
          makeActive,
          insertAtIndex: insertAtIndex ?? this.loadedChartIds.length
        },
        {
          chartData: result.data.chartData,
          metadata: result.data.metadata,
          history: result.data.history,
          currentHistoryIndex: result.data.currentHistoryIndex
        }
      );

      // Emit chart accessed event
      emitChartAccessed(chartId, metadata.name);

      const successResult = {
        success: true,
        chartId,
        message: `${metadata.name} loaded successfully`
      };

      this.notify(showToast, 'Chart Loaded', successResult.message!, 'success');
      return successResult;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load chart';
      const errorResult = {
        success: false,
        error: errorMessage
      };

      this.notify(showToast, 'Load Error', errorMessage, 'error', 6000);
      devError('ChartManager: Load error:', error);
      return errorResult;
    }
  }

  /**
   * Import chart data and load into workspace
   * Handles saving and loading in one operation
   */
  async importChartData(
    data: ParsedChartData,
    chartName: string = 'Imported Chart',
    options: ChartLoadOptions = {}
  ): Promise<LoadResult> {
    const { showToast = true } = options;

    try {
      // Save chart to storage
      const storageService = getStorageService();
      const saveResult = await storageService.saveChart(chartName, data);

      if (!saveResult.success || !saveResult.data) {
        throw new Error(saveResult.error || 'Failed to save imported chart');
      }

      // Load saved chart
      const loadResult = await this.loadChartFromStorage(
        saveResult.data,
        { name: chartName } as ChartMetadata,
        { ...options, showToast: false } // Handle toast ourselves
      );

      if (loadResult.success) {
        this.notify(showToast, 'Chart Imported', `"${chartName}" imported and loaded successfully`, 'success');
      }
      return loadResult;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to import chart';
      const errorResult = {
        success: false,
        error: errorMessage
      };

      this.notify(showToast, 'Import Error', errorMessage, 'error', 6000);
      devError('ChartManager: Import error:', error);
      return errorResult;
    }
  }

  /**
   * Create new chart with API and load into workspace
   * Professional chart creation flow
   */
  async createNewChart(
    data: ParsedChartData,
    chartName: string = 'New Chart',
    options: ChartCreateOptions = {}
  ): Promise<LoadResult> {
    const { makeActive = true, showToast = true } = options;

    devLog('🔄 [ChartManager] createNewChart called:', { chartName, hasData: !!data, makeActive });

    try {
      // Save chart to storage
      const storageService = getStorageService();
      devLog('💾 [ChartManager] Saving chart to storage...');
      const saveResult = await storageService.saveChart(chartName, data);
      devLog('💾 [ChartManager] Storage save result:', saveResult);

      if (!saveResult.success || !saveResult.data) {
        throw new Error(saveResult.error || 'Failed to save chart');
      }

      // Load chart into workspace
      devLog('🚀 [ChartManager] Loading chart into workspace...', { chartId: saveResult.data, makeActive });
      await this.loadChartToWorkspace(saveResult.data, { makeActive });
      devLog('✅ [ChartManager] Chart loaded into workspace successfully');

      // Emit chart saved and accessed events
      emitChartSaved(saveResult.data, chartName);
      if (makeActive) {
        emitChartAccessed(saveResult.data, chartName);
      }

      const result = {
        success: true,
        chartId: saveResult.data,
        message: `Chart "${chartName}" created and loaded into workspace`
      };

      this.notify(showToast, 'Chart Created', result.message!, 'success');
      return result;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create chart';
      const errorResult = {
        success: false,
        error: errorMessage
      };

      this.notify(showToast, 'Chart Creation Error', `Failed to create chart: ${errorMessage}`, 'error', 5000);
      devError('ChartManager: Creation error:', error);
      return errorResult;
    }
  }

  /**
   * Get workspace status information
   */
  getWorkspaceStatus() {
    return {
      loadedCount: this.loadedChartIds.length,
      maxCharts: this.workspace.maxOpenCharts,
      canLoadMore: this.loadedChartIds.length < this.workspace.maxOpenCharts,
      loadedChartIds: [...this.loadedChartIds]
    };
  }
}

/**
 * Factory function to create ChartManager instance
 * Integrates with MultipleChartsContext
 */
export function createChartManager(
  loadChartToWorkspace: (chartId: string, options?: any, preloadedData?: any) => Promise<void>,
  loadedChartIds: string[],
  workspace: { maxOpenCharts: number },
  showToast: (config: ToastConfig) => void,
  getLoadedContentHashes: () => Map<string, string> = () => new Map()
): ChartManager {
  return new ChartManager(
    loadChartToWorkspace,
    loadedChartIds,
    workspace,
    showToast,
    getLoadedContentHashes
  );
}